/* eslint-disable camelcase */
import axios from 'axios';
import { Ref } from 'vue';
import { storeToRefs } from 'pinia';
import EditMapping from '@/apps/order/classes/EditMapping';
import { hasMarginalPrice } from '@/apps/order/helper/marginalPrice';

import CompanySettingInterface from '@/apps/order/interfaces/CompanySettingInterface';
import SupplierTariffInterface from '@/apps/order/interfaces/SupplierTariffInterface';
import DataPreparedIntentInterface from '@/apps/order/interfaces/ordersCreate/DataPreparedIntentInterface';
import SupplierInterface from '@/apps/order/interfaces/SupplierInterface';
import BonusKindInterface from '@/apps/order/interfaces/BonusKindInterface';
import OrdersCreateCalculationResponseInterface from '@/apps/order/interfaces/OrdersCreateCalculationResponseInterface';
import DataPreparedCalculatorInterface from '@/apps/order/interfaces/ordersCreate/DataPreparedCalculatorInterface';
import DataPreparedAddressInterface from '@/apps/order/interfaces/ordersCreate/DataPreparedAddressInterface';
import DataPreparedConsumptionPointInterface
  from '@/apps/order/interfaces/ordersCreate/DataPreparedConsumptionPointInterface';
import DataPreparedPaymentInterface from '@/apps/order/interfaces/ordersCreate/DataPreparedPaymentInterface';
import { DataPreparedCustomFieldsInterface } from '@/apps/order/interfaces/ordersCreate/DataPreparedCustomFieldsInterface';
import DataPreparedOverviewInterface from '@/apps/order/interfaces/ordersCreate/DataPreparedOverviewInterface';
import CategoryInterface from '@/apps/order/interfaces/CategoryInterface';
import BillingTypeInterface from '@/apps/order/interfaces/BillingTypeInterface';
import { PriceUpdateParamsInterface, PriceUpdateVoucherParamsInterface } from '@/apps/order/interfaces/ordersCreate/PriceUpdateParamsInterface';
import BillingTypesParamsInterface from '@/apps/order/interfaces/ordersCreate/BillingTypesParamsInterface';
import DataPreparedParamsInterface from '@/apps/order/interfaces/ordersCreate/DataPreparedParamsInterface';

import { useOrdersCreateCategoriesStore } from '@/apps/order/stores/ordersCreate';

class OrderAPI {
  static fetchOrder(): Promise<EditMapping> {
    return new Promise((resolve, reject) => {
      axios.get<Record<string, any>>(`${window.API_URLS.getOrderByUuid}`).then((response) => {
        const { order } = response.data;
        const categoriesStore = useOrdersCreateCategoriesStore();
        const { categories } = storeToRefs(categoriesStore) as {
          categories: Ref<CategoryInterface[]>
        };

        const orderProduct = {
          ...order.product_data,
          prices_data: order.prices_data || [],
          category: categories.value?.find((categ) => (
            categ.id === order.product_data.categoryId
          )) || {},
          bonuses_data: order.bonuses_data || [],
          custom_price_data: order.custom_price_data || {},
          // Grenzpreis must travel with the product; else the save omits it and io.order overwrites the column with empty PriceData.
          marginal_price_data: hasMarginalPrice(order.marginal_price_data)
            ? order.marginal_price_data
            : null,
        };

        if (order.is_lead) {
          OrderAPI.calculatePrices(
            EditMapping.getCalculationParams(order, categories.value),
          ).then(({ products }: Record<string, any>) => {
            const hasLeadProduct = products.some(
              (p: Record<string, any>) => p.id === order.product_data.id,
            );

            if (!hasLeadProduct) {
              products.push(orderProduct);
            }

            resolve(new EditMapping(order, products, categories.value) as EditMapping);
          }).catch(() => {
            resolve(new EditMapping(order, [orderProduct], categories.value) as EditMapping);
          });
        } else {
          resolve(new EditMapping(order, [orderProduct], categories.value) as EditMapping);
        }
      }).catch((e) => {
        console.log(e);
        reject(e);
      });
    });
  }

  static fetchCategories(): Promise<CategoryInterface[]> {
    return new Promise((resolve, reject) => {
      axios.get<{ categories: CategoryInterface[] }>(`${window.API_URLS.categoryStepPrepare}`).then(({ data }) => {
        resolve(data.categories || []);
      }).catch((e) => {
        reject(e);
      });
    });
  }

  static fetchDataPreparedStepCalculator(
    params?: DataPreparedParamsInterface,
  ): Promise<DataPreparedCalculatorInterface> {
    return new Promise((resolve, reject) => {
      axios.get<DataPreparedCalculatorInterface>(
        `${window.API_URLS.calculatorStepPrepare}`,
        { params },
      ).then((response) => {
        resolve(response.data);
      }).catch((e) => {
        reject(e);
      });
    });
  }

  static fetchDataPreparedStepAddress(
    params?: DataPreparedParamsInterface,
  ): Promise<DataPreparedAddressInterface> {
    return new Promise((resolve, reject) => {
      axios.get<DataPreparedAddressInterface>(
        `${window.API_URLS.addressStepPrepare}`,
        { params },
      ).then((response) => {
        resolve(response.data);
      }).catch((e) => {
        reject(e);
      });
    });
  }

  static fetchDataPreparedStepConsumptionPoint(
    params?: DataPreparedParamsInterface,
  ): Promise<DataPreparedConsumptionPointInterface> {
    return new Promise((resolve, reject) => {
      axios.get<DataPreparedConsumptionPointInterface>(
        `${window.API_URLS.consumptionPointStepPrepare}`,
        { params },
      ).then((response) => {
        resolve(response.data);
      }).catch((e) => {
        reject(e);
      });
    });
  }

  static fetchDataPreparedStepPayment(
    params?: DataPreparedParamsInterface,
  ): Promise<DataPreparedPaymentInterface> {
    return new Promise((resolve, reject) => {
      axios.get<DataPreparedPaymentInterface>(
        `${window.API_URLS.paymentStepPrepare}`,
        { params },
      ).then((response) => {
        resolve(response.data);
      }).catch((e) => {
        reject(e);
      });
    });
  }

  static fetchDataPreparedStepCustomFields(
    params?: DataPreparedParamsInterface,
  ): Promise<DataPreparedCustomFieldsInterface> {
    return new Promise((resolve, reject) => {
      axios.get<DataPreparedCustomFieldsInterface>(
        `${window.API_URLS.customFieldsStepPrepare}`,
        { params },
      ).then((response) => {
        resolve(response.data);
      }).catch((e) => {
        reject(e);
      });
    });
  }

  static fetchDataPreparedStepOverview(
    params: Record<string, any>,
  ): Promise<DataPreparedOverviewInterface> {
    return new Promise((resolve, reject) => {
      axios.post<DataPreparedOverviewInterface>(
        `${window.API_URLS.overviewStepPrepare}`,
        params,
      ).then((response) => {
        resolve(response.data);
      }).catch((e) => {
        reject(e);
      });
    });
  }

  static calculatePrices(
    params: Record<string, any>,
  ): Promise<OrdersCreateCalculationResponseInterface> {
    return new Promise((resolve, reject) => {
      axios.get<OrdersCreateCalculationResponseInterface>(`${window.API_URLS.priceCalculationData}`, {
        params,
      }).then((response) => {
        resolve(response.data);
      }).catch((e) => {
        reject(e);
      });
    });
  }

  static fetchSalesChannels(brand: number): Promise<Record<string, any>[]> {
    return new Promise((resolve, reject) => {
      axios.get<Record<string, any>[]>(`${window.API_URLS.brandSalesChannels}`, {
        params: {
          brand_id: brand,
        },
      }).then((response) => {
        resolve(response.data);
      }).catch((e) => {
        reject(e);
      });
    });
  }

  static fetchSuppliers(categoryKey: string): Promise<SupplierInterface[]> {
    return new Promise<SupplierInterface[]>((resolve, reject) => {
      axios.post<{ suppliers: SupplierInterface[] }>(`${window.API_URLS.suppliers}`, {
        type: categoryKey === 'gas' ? 'gas' : 'power',
      }).then((response) => {
        resolve(response.data.suppliers || []);
      }).catch((e) => {
        reject(e);
      });
    });
  }

  static fetchSupplierProducts(
    categoryKey: string,
    supplierId: number,
  ): Promise<SupplierTariffInterface[]> {
    return new Promise<SupplierTariffInterface[]>((resolve, reject) => {
      axios.get<SupplierTariffInterface[]>(`${window.API_URLS.supplierProducts}`, {
        params: {
          type: categoryKey === 'gas' ? 'gas' : 'power',
          supplier_id: supplierId,
        },
      }).then((response) => {
        resolve(response.data || []);
      }).catch((e) => {
        reject(e);
      });
    });
  }

  static fetchCompanySettings(): Promise<CompanySettingInterface> {
    return new Promise((resolve, reject) => {
      axios.get<CompanySettingInterface>(`${window.API_URLS.companySettings}`)
        .then((response) => {
          resolve((response.data));
        })
        .catch((e) => {
          reject(e);
        });
    });
  }

  static fetchCitiesAndStreets(params: Record<string, any>): Promise<{
    cities: Array<{
      value: string,
      label: string,
    }>,
    streets: Array<{
      value: string,
      label: string,
    }>,
  }> {
    return new Promise((resolve, reject) => {
      axios.get<{
        cities: Array<{
          value: string,
          label: string,
        }>,
        streets: Array<{
          value: string,
          label: string,
        }>,
      }>(`${window.API_URLS.citiesAndStreets}`, {
        params,
      }).then((response) => {
        resolve(response.data as {
          cities: Array<{
            value: string,
            label: string,
          }>,
          streets: Array<{
            value: string,
            label: string,
          }>,
        });
      }).catch((e) => {
        reject(e);
      });
    });
  }

  static fetchBillingTypes(
    params?: BillingTypesParamsInterface,
  ): Promise<BillingTypeInterface[]> {
    return new Promise((resolve, reject) => {
      axios.get<BillingTypeInterface[]>(`${window.API_URLS.billingTypes}`, {
        params,
      }).then((response) => {
        resolve(response.data);
      }).catch((e) => {
        reject(e);
      });
    });
  }

  static fetchUpdatedPrices(
    params: PriceUpdateParamsInterface | PriceUpdateVoucherParamsInterface,
  ): Promise<Record<string, any>> {
    return new Promise((resolve, reject) => {
      axios.post(`${window.API_URLS.updatePrice}`, params).then((response) => {
        resolve(response.data);
      }).catch((e) => {
        reject(e);
      });
    });
  }

  static fetchAvailableBonuses(params: Record<string, any>): Promise<BonusKindInterface[]> {
    return new Promise((resolve, reject) => {
      axios.get<BonusKindInterface[]>(`${window.API_URLS.availableBonuses}`, {
        params,
      }).then((response) => {
        resolve(response.data || []);
      }).catch((e) => {
        reject(e);
      });
    });
  }

  static fetchIntentTexts(params: Record<string, any>): Promise<{
    params: Record<string, any>,
    response: DataPreparedIntentInterface,
  }> {
    return new Promise((resolve, reject) => {
      axios.post<DataPreparedIntentInterface>(`${window.API_URLS.intentTexts}`, [], {
        params,
      }).then((response) => {
        resolve({
          params,
          response: response.data,
        });
      }).catch((e) => {
        reject(e);
      });
    });
  }

  static fetchContractSummaryPdf(payload: Record<string, any>): Promise<{
    content: string,
    encoding: string,
    contentType: string,
  }> {
    return new Promise((resolve, reject) => {
      axios.post<{ content: string, encoding: string, contentType: string }>(
        `${window.API_URLS.documentSummary}`,
        payload,
      ).then((response) => {
        resolve(response.data);
      }).catch((e) => {
        reject(e);
      });
    });
  }
}

export default OrderAPI;
